import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Search,
  Plus,
  Eye,
  Heart,
  Download,
  FileText,
  Video,
  Image,
  Link as LinkIcon,
  Loader2,
  Filter,
  BookOpen,
  Clock,
  Users,
  Star,
  MoreHorizontal,
  CheckCircle2,
  XCircle,
  Archive,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const resourceTypeIcons = {
  pdf: FileText,
  document: FileText,
  video: Video,
  image: Image,
  link: LinkIcon,
  audio: BookOpen,
};

const resourceTypeNames = {
  pdf: 'PDF',
  document: 'Doküman',
  video: 'Video',
  image: 'Görsel',
  link: 'Bağlantı',
  audio: 'Ses',
};

const statusColors = {
  taslak: 'bg-slate-100 text-slate-700',
  beklemede: 'bg-yellow-100 text-yellow-700',
  guncel: 'bg-green-100 text-green-700',
  arsiv: 'bg-gray-100 text-gray-500',
};

const statusNames = {
  taslak: 'Taslak',
  beklemede: 'Onay Bekliyor',
  guncel: 'Yayında',
  arsiv: 'Arşiv',
};

const skillAreaNames = {
  akicilik: 'Akıcılık',
  cozumleme: 'Çözümleme',
  kelime: 'Kelime',
  anlama: 'Anlama',
  ses_bilgisi: 'Ses Bilgisi',
  yazma: 'Yazma',
};

export const ResourcesPage = () => {
  const { user, hasRole } = useAuth();
  const [resources, setResources] = useState([]);
  const [collections, setCollections] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({
    search: '',
    resource_type: 'all',
    skill_area: 'all',
    grade_level: 'all',
    status: 'all',
  });
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [newResource, setNewResource] = useState({
    title: '',
    description: '',
    resource_type: 'pdf',
    external_url: '',
    grade_levels: [],
    skill_areas: [],
    tags: '',
    duration_minutes: '',
  });

  const isAdmin = hasRole(['tenant_owner', 'center_director', 'reading_coach']);

  const fetchResources = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.resource_type !== 'all') params.append('resource_type', filters.resource_type);
      if (filters.skill_area !== 'all') params.append('skill_area', filters.skill_area);
      if (filters.grade_level !== 'all') params.append('grade_level', filters.grade_level);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (activeTab === 'favorites') params.append('favorites_only', 'true');
      
      const response = await fetch(`${API_URL}/api/resources?${params}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setResources(data);
      }
    } catch (error) {
      console.error('Failed to fetch resources:', error);
      toast.error('Kaynaklar yüklenirken hata oluştu');
    }
  };

  const fetchCollections = async () => {
    try {
      const response = await fetch(`${API_URL}/api/resources/collections/list`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setCollections(data);
      }
    } catch (error) {
      console.error('Failed to fetch collections:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/resources/stats`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchResources(), fetchCollections(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchResources();
    }
  }, [filters.resource_type, filters.skill_area, filters.grade_level, filters.status, activeTab]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchResources();
  };

  const handleToggleFavorite = async (resourceId) => {
    try {
      const response = await fetch(`${API_URL}/api/resources/${resourceId}/favorite`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        const result = await response.json();
        setResources(prev => prev.map(r => 
          r.resource_id === resourceId 
            ? { ...r, is_favorited: result.is_favorited, favorite_count: r.favorite_count + (result.is_favorited ? 1 : -1) }
            : r
        ));
        toast.success(result.message);
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleApprove = async (resourceId) => {
    try {
      const response = await fetch(`${API_URL}/api/resources/${resourceId}/approve`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Kaynak onaylandı');
        fetchResources();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Onaylama başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleArchive = async (resourceId) => {
    try {
      const response = await fetch(`${API_URL}/api/resources/${resourceId}/archive`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Kaynak arşivlendi');
        fetchResources();
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleAddResource = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    
    try {
      const payload = {
        ...newResource,
        tags: newResource.tags.split(',').map(t => t.trim()).filter(t => t),
        duration_minutes: newResource.duration_minutes ? parseInt(newResource.duration_minutes) : null,
      };
      
      const response = await fetch(`${API_URL}/api/resources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        toast.success('Kaynak oluşturuldu');
        setIsAddDialogOpen(false);
        setNewResource({
          title: '',
          description: '',
          resource_type: 'pdf',
          external_url: '',
          grade_levels: [],
          skill_areas: [],
          tags: '',
          duration_minutes: '',
        });
        fetchResources();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Kaynak oluşturulamadı');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setAddLoading(false);
    }
  };

  const toggleGradeLevel = (level) => {
    setNewResource(prev => ({
      ...prev,
      grade_levels: prev.grade_levels.includes(level)
        ? prev.grade_levels.filter(l => l !== level)
        : [...prev.grade_levels, level]
    }));
  };

  const toggleSkillArea = (area) => {
    setNewResource(prev => ({
      ...prev,
      skill_areas: prev.skill_areas.includes(area)
        ? prev.skill_areas.filter(a => a !== area)
        : [...prev.skill_areas, area]
    }));
  };

  const ResourceCard = ({ resource }) => {
    const TypeIcon = resourceTypeIcons[resource.resource_type] || FileText;
    
    return (
      <Card className="hover:shadow-md transition-shadow overflow-hidden">
        {resource.thumbnail_url && (
          <div className="h-40 overflow-hidden">
            <img 
              src={resource.thumbnail_url} 
              alt={resource.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <CardContent className={resource.thumbnail_url ? "pt-4" : "pt-6"}>
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <TypeIcon className="h-4 w-4 text-primary" />
              </div>
              <Badge variant="outline" className={statusColors[resource.status]}>
                {statusNames[resource.status]}
              </Badge>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to={`/resources/${resource.resource_id}`}>
                    <Eye className="mr-2 h-4 w-4" />
                    Görüntüle
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleToggleFavorite(resource.resource_id)}>
                  <Heart className={`mr-2 h-4 w-4 ${resource.is_favorited ? 'fill-red-500 text-red-500' : ''}`} />
                  {resource.is_favorited ? 'Favorilerden Çıkar' : 'Favorilere Ekle'}
                </DropdownMenuItem>
                {isAdmin && resource.status === 'beklemede' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleApprove(resource.resource_id)}>
                      <CheckCircle2 className="mr-2 h-4 w-4 text-green-600" />
                      Onayla
                    </DropdownMenuItem>
                  </>
                )}
                {isAdmin && resource.status === 'guncel' && (
                  <DropdownMenuItem onClick={() => handleArchive(resource.resource_id)}>
                    <Archive className="mr-2 h-4 w-4" />
                    Arşivle
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          <h3 className="font-semibold text-primary-900 line-clamp-2 mb-1">
            {resource.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
            {resource.description}
          </p>
          
          <div className="flex flex-wrap gap-1 mb-3">
            {resource.skill_areas?.slice(0, 2).map((area, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {skillAreaNames[area] || area}
              </Badge>
            ))}
            {resource.grade_levels?.length > 0 && (
              <Badge variant="outline" className="text-xs">
                {resource.grade_levels.join(', ')}. Sınıf
              </Badge>
            )}
          </div>
          
          <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                {resource.view_count}
              </span>
              <span className="flex items-center gap-1">
                <Heart className={`h-3 w-3 ${resource.is_favorited ? 'fill-red-500 text-red-500' : ''}`} />
                {resource.favorite_count}
              </span>
              <span className="flex items-center gap-1">
                <Download className="h-3 w-3" />
                {resource.download_count}
              </span>
            </div>
            {resource.duration_minutes && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {resource.duration_minutes} dk
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="resources-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary-900">
            Kaynak Kütüphanesi
          </h1>
          <p className="text-muted-foreground">
            {stats?.total || 0} kaynak • {stats?.pending_approval || 0} onay bekliyor
          </p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-resource-btn">
              <Plus className="mr-2 h-4 w-4" />
              Kaynak Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Yeni Kaynak Ekle</DialogTitle>
              <DialogDescription>
                Kütüphaneye yeni bir kaynak ekleyin
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddResource}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Başlık *</Label>
                  <Input
                    id="title"
                    value={newResource.title}
                    onChange={(e) => setNewResource({...newResource, title: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Açıklama *</Label>
                  <Textarea
                    id="description"
                    value={newResource.description}
                    onChange={(e) => setNewResource({...newResource, description: e.target.value})}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Kaynak Türü</Label>
                    <Select
                      value={newResource.resource_type}
                      onValueChange={(value) => setNewResource({...newResource, resource_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(resourceTypeNames).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="duration">Süre (dk)</Label>
                    <Input
                      id="duration"
                      type="number"
                      value={newResource.duration_minutes}
                      onChange={(e) => setNewResource({...newResource, duration_minutes: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="external_url">Bağlantı URL (video/link için)</Label>
                  <Input
                    id="external_url"
                    type="url"
                    value={newResource.external_url}
                    onChange={(e) => setNewResource({...newResource, external_url: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sınıf Düzeyleri</Label>
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(level => (
                      <Button
                        key={level}
                        type="button"
                        variant={newResource.grade_levels.includes(level) ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleGradeLevel(level)}
                      >
                        {level}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Beceri Alanları</Label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(skillAreaNames).map(([value, label]) => (
                      <Button
                        key={value}
                        type="button"
                        variant={newResource.skill_areas.includes(value) ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleSkillArea(value)}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tags">Etiketler (virgülle ayırın)</Label>
                  <Input
                    id="tags"
                    value={newResource.tags}
                    onChange={(e) => setNewResource({...newResource, tags: e.target.value})}
                    placeholder="örn: akıcılık, 1. sınıf, pratik"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  İptal
                </Button>
                <Button type="submit" disabled={addLoading || !newResource.title || !newResource.description}>
                  {addLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Oluştur
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-primary-900">{stats.total}</div>
              <p className="text-sm text-muted-foreground">Toplam Kaynak</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{stats.by_status?.guncel || 0}</div>
              <p className="text-sm text-muted-foreground">Yayında</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">{stats.pending_approval}</div>
              <p className="text-sm text-muted-foreground">Onay Bekliyor</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-secondary">{collections.length}</div>
              <p className="text-sm text-muted-foreground">Koleksiyon</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">Tüm Kaynaklar</TabsTrigger>
          <TabsTrigger value="favorites">Favorilerim</TabsTrigger>
          <TabsTrigger value="collections">Koleksiyonlar</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Kaynak ara..."
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                    className="pl-10"
                  />
                </div>
                <Select value={filters.resource_type} onValueChange={(value) => setFilters({...filters, resource_type: value})}>
                  <SelectTrigger className="w-full md:w-[150px]">
                    <SelectValue placeholder="Tür" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Türler</SelectItem>
                    {Object.entries(resourceTypeNames).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filters.skill_area} onValueChange={(value) => setFilters({...filters, skill_area: value})}>
                  <SelectTrigger className="w-full md:w-[150px]">
                    <SelectValue placeholder="Beceri" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Beceriler</SelectItem>
                    {Object.entries(skillAreaNames).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filters.grade_level} onValueChange={(value) => setFilters({...filters, grade_level: value})}>
                  <SelectTrigger className="w-full md:w-[120px]">
                    <SelectValue placeholder="Sınıf" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Sınıflar</SelectItem>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(level => (
                      <SelectItem key={level} value={String(level)}>{level}. Sınıf</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isAdmin && (
                  <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value})}>
                    <SelectTrigger className="w-full md:w-[140px]">
                      <SelectValue placeholder="Durum" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Durumlar</SelectItem>
                      {Object.entries(statusNames).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button type="submit" variant="secondary">
                  <Filter className="mr-2 h-4 w-4" />
                  Filtrele
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Resources Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {resources.map(resource => (
              <ResourceCard key={resource.resource_id} resource={resource} />
            ))}
          </div>
          
          {resources.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Kaynak bulunamadı
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="favorites">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {resources.filter(r => r.is_favorited).map(resource => (
              <ResourceCard key={resource.resource_id} resource={resource} />
            ))}
          </div>
          
          {resources.filter(r => r.is_favorited).length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Henüz favori kaynağınız yok
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="collections" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {collections.map(collection => (
              <Card key={collection.collection_id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-2">
                    <div className="h-10 w-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                      <BookOpen className="h-5 w-5 text-secondary" />
                    </div>
                    {!collection.is_public && (
                      <Badge variant="outline">Özel</Badge>
                    )}
                  </div>
                  <h3 className="font-semibold text-primary-900 mb-1">{collection.name}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{collection.description}</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{collection.resource_count} kaynak</span>
                    <span className="text-muted-foreground">{collection.creator_name}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {collections.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Henüz koleksiyon oluşturulmamış
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ResourcesPage;
